/**
 * The app preview shown beside the hero.
 *
 * Decorative: the wrapper is aria-hidden, and the hero's own copy carries the
 * meaning. A screen reader reading out a fake grocery list adds nothing but
 * noise, so it is excluded rather than described item by item.
 *
 * Two brand rules are load-bearing here. Categories are mono uppercase labels
 * with a hairline rule and a count — never colour swatches, because in this
 * system colour means bought or broken and cannot also be decoration. And the
 * unticked checkbox uses --control-edge, not --line: a state indicator needs
 * 3:1, while a divider is built to sit about 1.1:1 off its surface.
 */
import { Logo } from './Logo';
import styles from './PhoneMockup.module.css';

export interface MockupRow {
  name: string;
  by?: string;
  qty?: string;
  /** Shows the dashed stand-in for an attached photo. */
  photo?: boolean;
}

export interface MockupGroup {
  label: string;
  count: string;
  rows: MockupRow[];
}

interface PhoneMockupProps {
  houseLabel: string;
  houseName: string;
  houseMeta: string;
  addLabel: string;
  shopLabel: string;
  groups: MockupGroup[];
  tabs: { list: string; feed: string; house: string; settings: string };
}

export function PhoneMockup({
  houseLabel,
  houseName,
  houseMeta,
  addLabel,
  shopLabel,
  groups,
  tabs,
}: PhoneMockupProps) {
  return (
    <div className={styles.wrap} aria-hidden="true">
      <div className={styles.backdrop} />

      <div className={styles.device}>
        <div className={styles.appHeader}>
          <span className={styles.houseBlock}>
            <span className={`${styles.houseLabel} overline`}>{houseLabel}</span>
            <span className={styles.houseName}>{houseName}</span>
            <span className={`${styles.houseMeta} numeric`}>{houseMeta}</span>
          </span>

          <span className={styles.headerRight}>
            <span className={styles.avatars}>
              {/* Initials take whichever of cream or ink measures higher against
                  the fill, per the spec — not whichever a lightness threshold
                  suggests. Cream wins on both of these. */}
              <span className={styles.avatar} style={{ background: '#1B1A18' }}>
                MJ
              </span>
              <span
                className={`${styles.avatar} ${styles.avatarOverlap}`}
                style={{ background: '#5A5750' }}
              >
                S
              </span>
            </span>
            <Logo size={24} />
          </span>
        </div>

        <div className={styles.list}>
          {groups.map((group) => (
            <div key={group.label} className={styles.group}>
              <div className={styles.categoryRow}>
                <span className={styles.categoryName}>{group.label}</span>
                <span className={styles.categoryRule} />
                <span className={styles.categoryCount}>{group.count}</span>
              </div>

              {group.rows.map((row) => (
                // No checkbox at home: the swipe is the gesture, and a
                // bought row leaves the list — the mockup mirrors the app,
                // not the app's first draft.
                <div key={row.name} className={styles.row}>
                  <span className={styles.rowBody}>
                    <span className={styles.rowName}>{row.name}</span>
                    {row.by ? <span className={styles.rowBy}>{row.by}</span> : null}
                  </span>
                  {row.qty ? <span className={styles.qty}>{row.qty}</span> : null}
                  {row.photo ? <span className={styles.photoSlot} /> : null}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className={styles.actions}>
          <span className={styles.addButton}>{addLabel}</span>
          <span className={styles.shopButton}>{shopLabel}</span>
        </div>

        <div className={styles.tabBar}>
          {/* The tab bar carries an icon AND a label — never labels alone. */}
          <Tab icon={<ListIcon />} label={tabs.list} active />
          <Tab icon={<FeedIcon />} label={tabs.feed} />
          <Tab icon={<HouseIcon />} label={tabs.house} />
          <Tab icon={<SettingsIcon />} label={tabs.settings} />
        </div>
      </div>
    </div>
  );
}

function Tab({ icon, label, active }: { icon: React.ReactNode; label: string; active?: boolean }) {
  return (
    <span className={`${styles.tab} ${active ? styles.tabActive : ''}`}>
      {icon}
      <span className={styles.tabLabel}>{label}</span>
    </span>
  );
}

/* Icons: geometric, 2.4px stroke, round caps and joins, per the spec. */
const strokeProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2.4,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const ListIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" {...strokeProps}>
    <path d="M8 6h13M8 12h13M8 18h13" />
    <path d="M3 6l1.5 1.5L7 5" />
    <path d="M3 12l1.5 1.5L7 10.5" />
    <path d="M3 18l1.5 1.5L7 16.5" />
  </svg>
);

const FeedIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" {...strokeProps}>
    <path d="M4 20V13M10 20V5M16 20v-9M22 20v-5" />
  </svg>
);

const HouseIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" {...strokeProps}>
    <path d="M4 11l8-6 8 6v8a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 19z" />
  </svg>
);

const SettingsIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" {...strokeProps}>
    <path d="M4 6.5h16M4 12h16M4 17.5h16" />
    <circle cx="9.2" cy="6.5" r="2.3" fill="currentColor" stroke="none" />
    <circle cx="15" cy="12" r="2.3" fill="currentColor" stroke="none" />
    <circle cx="7.4" cy="17.5" r="2.3" fill="currentColor" stroke="none" />
  </svg>
);
